import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { Bar, BarChart, Cell, LabelList, XAxis, YAxis } from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Users, Plus, Search, List, Cake,
  Globe, Building2,
  Briefcase, Tag, Layers, RefreshCw, Info,
} from 'lucide-react'
import {
  useDashboard,
  usePaises,
  useLaboratorios,
  useCargos,
} from '../hooks/useBdClientes'
import { StatsCard } from '../components/StatsCard'
import type { FiltrosDashboard, ChartItem } from '../types'

// Tonos teal de oscuro a claro (light mode)
const TEAL_LIGHT = [
  '#134e4a', '#115e59', '#0f766e', '#0d9488', '#14b8a6',
  '#2dd4bf', '#5eead4', '#99f6e4', '#ccfbf1', '#f0fdfa',
]
// Tonos teal de claro a oscuro (dark mode)
const TEAL_DARK = [
  '#ccfbf1', '#99f6e4', '#5eead4', '#2dd4bf', '#14b8a6',
  '#0d9488', '#0f766e', '#115e59', '#134e4a', '#0c3c38',
]

function getBarColor(index: number, total: number, isDark: boolean): string {
  const palette = isDark ? TEAL_DARK : TEAL_LIGHT
  const mappedIndex = Math.round((index / Math.max(total - 1, 1)) * (palette.length - 1))
  return palette[Math.min(mappedIndex, palette.length - 1)]
}

function BarChartCard({
  title,
  icon: Icon,
  data,
  isDark,
  className,
  showInfo: showInfoProp,
}: {
  title: string
  icon: React.ElementType
  data: ChartItem[]
  isDark: boolean
  className?: string
  showInfo?: string
}) {
  const [infoOpen, setInfoOpen] = useState(false)
  const chartConfig = {
    total: { label: 'Clientes', color: '#14b8a6' },
  } satisfies ChartConfig

  if (!data || data.length === 0) {
    return (
      <Card className={`border border-gray-300 dark:border-gray-700 ${className || ''}`}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Icon className="h-4 w-4 text-teal-500" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
            <Icon className="h-8 w-8 mb-2 opacity-30" />
            <p className="text-sm">No hay datos</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={`border border-gray-300 dark:border-gray-700 ${className || ''}`}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            {title}
          </CardTitle>
          {showInfoProp && (
            <>
              <button
                onClick={() => setInfoOpen(!infoOpen)}
                className="text-muted-foreground hover:text-foreground transition-colors ml-2"
              >
                <Info className="h-4 w-4" />
              </button>
              {infoOpen && (
                <span className="text-muted-foreground ml-2 text-sm animate-in slide-in-from-left-2 duration-200">
                  {showInfoProp}
                </span>
              )}
            </>
          )}
        </div>
        <Icon className="h-4 w-4 text-teal-500" />
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[280px] w-full">
          <BarChart
            accessibilityLayer
            data={data}
            layout="vertical"
            margin={{ left: 0, right: 50 }}
          >
            <XAxis type="number" dataKey="total" hide />
            <YAxis
              dataKey="nombre"
              type="category"
              tickLine={false}
              tickMargin={10}
              axisLine={false}
              width={120}
              tickFormatter={(value) =>
                value.length > 15 ? value.slice(0, 15) + '...' : value
              }
            />
            <ChartTooltip
              cursor={false}
              content={<ChartTooltipContent hideLabel />}
            />
            <Bar dataKey="total" radius={4}>
              {data.map((_, index) => (
                <Cell key={index} fill={getBarColor(index, data.length, isDark)} />
              ))}
              <LabelList
                dataKey="total"
                position="right"
                offset={8}
                className="fill-foreground"
                fontSize={12}
                formatter={(value: number) => value.toLocaleString()}
              />
            </Bar>
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}

function estadoBadge(estado: string) {
  switch (estado) {
    case 'activo':
      return <Badge className="badge-gradient hover:opacity-90">Activo</Badge>
    case 'inactivo':
      return <Badge variant="outline" className="text-gray-500 border-gray-400">Inactivo</Badge>
    case 'prospecto':
      return <Badge variant="outline" className="text-yellow-600 dark:text-teal-500 border-yellow-500">Prospecto</Badge>
    default:
      return <Badge variant="outline">{estado}</Badge>
  }
}

export function DashboardBdClientesPage() {
  useDocumentTitle('BD Clientes - Dashboard')
  const navigate = useNavigate()
  const [isDark, setIsDark] = useState(false)

  useEffect(() => {
    const root = document.documentElement
    setIsDark(root.classList.contains('dark'))
    const observer = new MutationObserver(() => {
      setIsDark(root.classList.contains('dark'))
    })
    observer.observe(root, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])

  const [searchInput, setSearchInput] = useState('')
  const [filtros, setFiltros] = useState<FiltrosDashboard>({})

  const { data: dashboard, isLoading } = useDashboard(filtros)
  const { data: paises } = usePaises(true)
  const { data: laboratorios } = useLaboratorios(filtros.pais_id)
  const { data: cargos } = useCargos(undefined, true)

  const handleSearch = () => {
    setFiltros(prev => ({ ...prev, search: searchInput || undefined }))
  }

  const handleFilterChange = (key: string, value: string) => {
    setFiltros(prev => ({
      ...prev,
      [key]: value === 'all' ? undefined : (key.endsWith('_id') ? Number(value) : value),
    }))
    if (key === 'pais_id') {
      setFiltros(prev => ({ ...prev, laboratorio_id: undefined }))
    }
  }

  const clearFilters = () => {
    setSearchInput('')
    setFiltros({})
  }

  const hasActiveFilters = Object.values(filtros).some(v => v !== undefined)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Users className="h-6 w-6" />
          Dashboard de Clientes
        </h1>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/bd-clientes/seguimiento')}
            className="border-gray-700 text-gray-700 hover:bg-gray-50 dark:border-gray-500 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            <List className="mr-2 h-4 w-4" />
            Ver Lista
          </Button>
          <Button
            size="sm"
            onClick={() => navigate('/bd-clientes/crear')}
            className="bg-teal-800 hover:bg-teal-900 text-white"
          >
            <Plus className="mr-2 h-4 w-4" />
            Nuevo Cliente
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      ) : dashboard && (
        <div className="grid gap-4 md:grid-cols-4">
          <StatsCard
            title="Total Clientes"
            value={dashboard.stats.total}
            icon={Users}
            description={`Activos: ${dashboard.stats.activos} | Inactivos: ${dashboard.stats.inactivos}`}
          />
          <StatsCard
            title="Países"
            value={dashboard.stats.total_paises}
            icon={Globe}
            description="Activos en el sistema"
          />
          <StatsCard
            title="Laboratorios"
            value={dashboard.stats.total_laboratorios}
            icon={Building2}
            description="Farmacéuticos activos"
          />
          <StatsCard
            title="Cumpleaños"
            value={dashboard.stats.cumpleanos_proximos}
            icon={Cake}
            description="Próximos 30 días"
          />
        </div>
      )}

      {/* Filtros */}
      <Card className="p-4 border border-gray-300 dark:border-gray-700">
        <div className="flex flex-col gap-4">
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                placeholder="Buscar por nombre, email, teléfono, marca..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <Button variant="outline" onClick={handleSearch}>
              <Search className="h-4 w-4" />
            </Button>
            {hasActiveFilters && (
              <Button variant="ghost" onClick={clearFilters} title="Limpiar filtros">
                <RefreshCw className="h-4 w-4" />
              </Button>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Select
              value={filtros.pais_id?.toString() || 'all'}
              onValueChange={(v) => handleFilterChange('pais_id', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="País" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los países</SelectItem>
                {paises?.map((p) => (
                  <SelectItem key={p.id} value={p.id.toString()}>{p.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filtros.laboratorio_id?.toString() || 'all'}
              onValueChange={(v) => handleFilterChange('laboratorio_id', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Laboratorio" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los laboratorios</SelectItem>
                {laboratorios?.map((l) => (
                  <SelectItem key={l.id} value={l.id.toString()}>{l.razon_social}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filtros.cargo_id?.toString() || 'all'}
              onValueChange={(v) => handleFilterChange('cargo_id', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Cargo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los cargos</SelectItem>
                {cargos?.map((c) => (
                  <SelectItem key={c.id} value={c.id.toString()}>{c.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filtros.estado || 'all'}
              onValueChange={(v) => handleFilterChange('estado', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="activo">Activo</SelectItem>
                <SelectItem value="inactivo">Inactivo</SelectItem>
                <SelectItem value="prospecto">Prospecto</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Charts Row 1: Cargo + Línea */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-[350px]" />
          <Skeleton className="h-[350px]" />
        </div>
      ) : dashboard && (
        <div className="grid gap-4 md:grid-cols-2">
          <BarChartCard
            title="Clientes por Cargo"
            icon={Briefcase}
            data={dashboard.charts.por_cargo}
            isDark={isDark}
            showInfo="Distribución por cargo"
          />
          <BarChartCard
            title="Clientes por Línea"
            icon={Layers}
            data={dashboard.charts.por_linea}
            isDark={isDark}
            showInfo="Distribución por línea"
          />
        </div>
      )}

      {/* Charts Row 2: Laboratorio + País + Marca */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-3">
          <Skeleton className="h-[350px]" />
          <Skeleton className="h-[350px]" />
          <Skeleton className="h-[350px]" />
        </div>
      ) : dashboard && (
        <div className="grid gap-4 md:grid-cols-3">
          <BarChartCard
            title="Por Laboratorio"
            icon={Building2}
            data={dashboard.charts.por_laboratorio}
            isDark={isDark}
            showInfo="Top laboratorios"
          />
          <BarChartCard
            title="Por País"
            icon={Globe}
            data={dashboard.charts.por_pais}
            isDark={isDark}
            showInfo="Distribución geográfica"
          />
          <BarChartCard
            title="Por Marca"
            icon={Tag}
            data={dashboard.charts.por_marca}
            isDark={isDark}
            showInfo="Top marcas"
          />
        </div>
      )}

      {/* Bottom: Clientes Recientes + Cumpleaños */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-3">
          <Skeleton className="h-[300px] md:col-span-2" />
          <Skeleton className="h-[300px]" />
        </div>
      ) : dashboard && (
        <div className="grid gap-4 md:grid-cols-3">
          {/* Clientes Recientes */}
          <Card className="border border-gray-300 dark:border-gray-700 md:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Users className="h-4 w-4 text-teal-500" />
                Clientes Recientes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dashboard.clientes_recientes.length > 0 ? (
                <Table>
                  <TableHeader className="bg-gray-100 dark:bg-[hsl(220,15%,16%)] [&_tr]:hover:bg-gray-100 dark:[&_tr]:hover:bg-[hsl(220,15%,16%)]">
                    <TableRow>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Laboratorio</TableHead>
                      <TableHead>País</TableHead>
                      <TableHead>Cargo</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Fecha</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dashboard.clientes_recientes.map((cliente) => (
                      <TableRow
                        key={cliente.id}
                        className="cursor-pointer"
                        onClick={() => navigate(`/bd-clientes/${cliente.id}`)}
                      >
                        <TableCell className="font-medium text-teal-700 dark:text-teal-400">
                          {cliente.nombre_contacto}
                        </TableCell>
                        <TableCell>{cliente.laboratorio_nombre}</TableCell>
                        <TableCell>{cliente.pais_nombre}</TableCell>
                        <TableCell>{cliente.cargo_nombre}</TableCell>
                        <TableCell>{estadoBadge(cliente.estado)}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(cliente.fecha_creacion).toLocaleDateString('es-EC')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                  <Users className="h-8 w-8 mb-2 opacity-30" />
                  <p className="text-sm mb-3">No hay clientes registrados</p>
                  <Button
                    size="sm"
                    onClick={() => navigate('/bd-clientes/crear')}
                    className="bg-teal-800 hover:bg-teal-900 text-white"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Agregar Cliente
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Cumpleaños Próximos */}
          <Card className="border border-gray-300 dark:border-gray-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Cake className="h-4 w-4 text-teal-500" />
                Cumpleaños Próximos
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dashboard.cumpleanos_proximos.length > 0 ? (
                <div className="space-y-4">
                  {dashboard.cumpleanos_proximos.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg p-2 -mx-2 transition-colors"
                      onClick={() => navigate(`/bd-clientes/${item.id}`)}
                    >
                      <Cake className="h-4 w-4 text-teal-500" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{item.nombre_contacto}</p>
                        <p className="text-xs text-muted-foreground">{item.cumpleanos_formato}</p>
                        <p className="text-xs text-muted-foreground">{item.laboratorio_nombre}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                  <Cake className="h-8 w-8 mb-2 opacity-30" />
                  <p className="text-sm">No hay cumpleaños próximos</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
